// QR ordering, the restaurant's side: guests' requests waiting for a waiter
// (accept or send back), and the QR codes for the tables in Settings.
import { acceptRequest, decideRequest, formatQty, openOrder, requestExpired } from '@ims/core'
import type { GuestRequest, TenantSettingsRow } from '@ims/data'
import { tenantApi } from '@ims/remote'
import {
  arrivalClass,
  Badge,
  Button,
  buttonClass,
  Card,
  cx,
  Modal,
  Switch,
  useArrivals,
  useToast,
} from '@ims/ui'
import {
  BellRing,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Printer,
  QrCode as QrIcon,
  ReceiptText,
  RefreshCw,
} from '@ims/ui/icons'
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { errorMessage, useEntitlements, useLive, useOnline } from '../hooks.ts'
import {
  isOpenOrder,
  openFor,
  orderRow,
  requestItemRows,
  requestProducts,
  requestRow,
} from '../lib/orders.ts'
import { updateOp } from '../lib/rows.ts'
import { printDocument } from '../print.tsx'
import { QrCode, QrSheet, type TableCode } from '../qr.tsx'
import { useSession, useUser } from '../session.tsx'
import { useCan, useRepo, useStore, useWorkspace } from '../workspace.tsx'

/**
 * Requests waiting for a waiter. Accepting an order adds its lines to the
 * table's bill as new items (then "Send to kitchen" as usual); a call for the
 * waiter or the bill is just marked done.
 */
export function GuestRequests({
  requests: all,
  settings,
  now,
  showTable = true,
  bare = false,
  reveal = false,
  grouped = false,
  decorate,
  quiet,
}: {
  requests: GuestRequest[]
  settings: TenantSettingsRow | null | undefined
  now: Date
  /** False on a table's own order screen. */
  showTable?: boolean
  /** Inside the alerts panel: a plain frame, no card or margin. */
  bare?: boolean
  /** The strip showed up while the page was open: it fades in. */
  reveal?: boolean
  /** Rows only, inside one of the bell's groups (the group has the heading and frame). */
  grouped?: boolean
  /** A line under a row: its server and how long it has waited (the bell). */
  decorate?: (g: GuestRequest) => ReactNode
  /** Someone else's table still within its wait: faded, its buttons still work. */
  quiet?: (g: GuestRequest) => boolean
}) {
  const repo = useRepo()
  const toast = useToast()
  const { current } = useWorkspace()
  const canServe = useCan('waiter')
  const online = useOnline()
  const { engine } = useStore()
  const [busy, setBusy] = useState<string | null>(null)
  // One sent to a free table that nobody answered in time drops out (the guest is told).
  const requests = all.filter((g) => !requestExpired(g.request, now.getTime()))
  // A request that comes in while the list shows slides in.
  const arrived = useArrivals(requests.map((g) => g.request.id))
  const me = useUser().id
  if (requests.length === 0) return null

  const run = async (id: string, work: () => Promise<void>) => {
    setBusy(id)
    try {
      await work()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(null)
    }
  }
  const context = () => ({ tenantId: current!.tenant_id, now: new Date().toISOString() })

  const accept = (shown: GuestRequest) =>
    run(shown.request.id, async () => {
      if (shown.request.kind !== 'order') {
        await repo.commit(decideRequest(context(), requestRow(shown.request), 'accepted').ops)
        return
      }
      // Guests can change a waiting round: take it as it is now, not as it was drawn.
      // (The server refuses an accept of an older version: request_changed.)
      if (online) await engine.syncNow().catch(() => {})
      const g = (await repo.orders.requests()).find((r) => r.request.id === shown.request.id)
      if (!g) {
        toast.warning('The guests changed or cancelled this order', {
          detail: 'Look at it again.',
        })
        return
      }
      // The order as it is right now: the table may have been merged or moved on.
      let detail = g.request.order_id ? await repo.orders.detail(g.request.order_id) : null
      if (!detail || !isOpenOrder(detail.order)) {
        const open = (await repo.orders.open()).find((o) => o.order.table_id === g.request.table_id)
        detail = open ? await repo.orders.detail(open.order.id) : null
      }
      // Sent to a free table: accepting starts the table, in the same commit.
      const started =
        !detail && g.request.order_id === null
          ? openOrder(context(), { tableId: g.request.table_id, covers: 1, serverId: me })
          : null
      const order = started?.order ?? (detail && isOpenOrder(detail.order) ? detail.order : null)
      if (!order) {
        toast.error('This table has no open order any more. Send the request back.')
        return
      }
      const products = await requestProducts(
        repo,
        g.items.map((i) => i.product_id),
        settings?.default_tax_rate_id ?? null,
      )
      const built = acceptRequest(
        context(),
        orderRow(order),
        requestRow(g.request),
        requestItemRows(g.items),
        products,
      )
      await repo.commit([...(started?.ops ?? []), ...built.ops])
      const missing = g.items.length - built.items.length
      const done = started ? 'Table started and added to the bill.' : 'Added to the bill.'
      toast.success(
        missing > 0
          ? `${done} ${missing} item${missing === 1 ? ' is' : 's are'} no longer on the menu.`
          : `${done} Send it to the kitchen when ready.`,
      )
    })
  const sendBack = (g: GuestRequest) =>
    run(g.request.id, () =>
      repo.commit(decideRequest(context(), requestRow(g.request), 'rejected').ops).then(() => {}),
    )

  const list = (
    <ul className={cx('divide-y divide-border', grouped && 'border-t border-border')}>
      {requests.map((g) => {
        const where = g.tableName ?? 'No table'
        return (
          <li
            key={g.request.id}
            className={cx(
              'flex flex-wrap items-start gap-3 px-4 py-3',
              arrived.has(g.request.id) && arrivalClass,
              quiet?.(g) && 'opacity-80',
            )}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">
                {showTable &&
                  (g.request.order_id ? (
                    <Link
                      to={`/tables/${g.request.order_id}`}
                      className="text-primary hover:underline"
                    >
                      {where}
                    </Link>
                  ) : (
                    // A free table: accepting the request starts it.
                    <>
                      {where} <Badge tone="success">Free table</Badge>
                    </>
                  ))}
                {showTable && ' · '}
                {g.request.kind === 'call'
                  ? 'Calling the waiter'
                  : g.request.kind === 'bill'
                    ? 'Asking for the bill'
                    : 'Order from the table'}
                <span className="font-normal text-muted">
                  {' '}
                  · {openFor(g.request.created_at, now)} ago
                </span>
              </p>
              {decorate?.(g)}
              {g.items.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-sm">
                  {g.items.map((i) => (
                    <li key={i.id}>
                      {formatQty(i.qty_milli)}× {i.description}
                      {i.note && <span className="italic text-muted"> · {i.note}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {canServe && (
              <div className="flex shrink-0 gap-2">
                {g.request.kind === 'order' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    aria-label={`Send back the request from ${where}`}
                    onClick={() => void sendBack(g)}
                  >
                    Send back
                  </Button>
                )}
                <Button
                  size="sm"
                  loading={busy === g.request.id}
                  disabled={busy !== null && busy !== g.request.id}
                  icon={g.request.kind === 'bill' ? <ReceiptText className="size-4" /> : undefined}
                  aria-label={`${g.request.kind === 'order' ? 'Accept' : 'Done'}: request from ${where}`}
                  onClick={() => void accept(g)}
                >
                  {g.request.kind === 'order' ? 'Accept' : 'Done'}
                </Button>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
  if (grouped) return list
  return (
    <Card
      className={cx(
        'border-primary/30 p-0',
        bare ? 'shadow-none' : 'mb-4',
        reveal && 'animate-fade-in',
      )}
    >
      <h2 className="flex items-center gap-2 border-b border-border px-4 py-3 text-sm font-semibold">
        <BellRing className="size-4 text-primary" />
        {requests.length === 1
          ? '1 request from a guest'
          : `${requests.length} requests from guests`}
      </h2>
      {list}
    </Card>
  )
}

/** Whether this restaurant's tables take guests' requests (the switch and the plan). */
export function useQrOrdering(settings: TenantSettingsRow | null | undefined): boolean {
  const inPlan = useEntitlements()?.limits?.qr_ordering !== 0
  return Boolean(settings?.restaurant && settings.guest_ordering && inPlan)
}

/** One table's QR code on the screen, to show a guest or to print for the table. */
export function TableQrDialog({
  table,
  settings,
  onClose,
}: {
  table: { id: string; name: string }
  settings: TenantSettingsRow
  onClose: () => void
}) {
  const online = useOnline()
  const { platform } = useSession()
  const { current } = useWorkspace()
  const tenantId = current!.tenant_id
  const [code, setCode] = useState<TableCode | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!online) return
    let stale = false
    tenantApi(platform.supabase)
      .tableQrCode(tenantId, table.id)
      .then(({ token }) => {
        if (stale) return
        setCode({ table_id: table.id, name: table.name, url: `${platform.menuUrl}/t/${token}` })
        setError(null)
      })
      .catch((e: unknown) => {
        if (!stale) setError(errorMessage(e))
      })
    return () => {
      stale = true
    }
  }, [online, platform, tenantId, table.id, table.name])

  return (
    <Modal
      open
      onClose={onClose}
      title={`QR code for ${table.name}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            icon={<Printer className="size-4" />}
            disabled={!code}
            onClick={() =>
              code &&
              printDocument(<QrSheet codes={[code]} businessName={settings.business_name} />, 'a4')
            }
          >
            Print
          </Button>
        </>
      }
    >
      {code ? (
        <div className="text-center">
          <div className="mx-auto w-full max-w-64 overflow-hidden rounded-xl border border-border">
            <QrCode value={code.url} label={`QR code for ${table.name}`} />
          </div>
          <p className="mt-3 text-sm text-muted">
            Guests scan this to see the menu and send their order.
          </p>
          <div className="mt-3 flex justify-center">
            <TableLink name={table.name} url={code.url} />
          </div>
        </div>
      ) : (
        <p className="py-8 text-center text-sm text-muted">
          {!online ? 'Connect to the internet to see the code.' : (error ?? 'Getting the code…')}
        </p>
      )}
    </Modal>
  )
}

/**
 * A table's guest link as two actions, never as text: the address is long, and
 * whoever has it can see that table's order, so it is copied or opened on purpose.
 */
function TableLink({ name, url }: { name: string; url: string }) {
  const toast = useToast()
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success(`Link for ${name} copied`)
    } catch {
      toast.error('This browser could not copy the link. Use Open, then copy the address.')
    }
  }
  return (
    <span className="flex shrink-0 items-center gap-1">
      <Button
        size="sm"
        variant="ghost"
        icon={<Copy className="size-3.5" />}
        aria-label={`Copy the link for ${name}`}
        onClick={() => void copy()}
      >
        Copy link
      </Button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Menu link for ${name}`}
        className={buttonClass({ variant: 'ghost', size: 'sm' })}
      >
        <ExternalLink className="size-3.5" aria-hidden />
        Open
      </a>
    </span>
  )
}

/** The guest page's accents: the names the server keeps, and the light shade guests see. */
const GUEST_ACCENTS = [
  { id: 'green', label: 'Leaf green', hex: '#2f5d3a' },
  { id: 'terracotta', label: 'Terracotta', hex: '#b5492a' },
  { id: 'indigo', label: 'Indigo', hex: '#3f3d9b' },
  { id: 'saffron', label: 'Saffron', hex: '#8a5a0a' },
] as const

/** The order of the menu's sections on the guest's page: categories, moved up and down. */
function MenuSections({
  disabled,
  act,
}: {
  disabled: boolean
  act: (work: () => Promise<void>) => Promise<void>
}) {
  const repo = useRepo()
  const all = useLive(
    async (r) => (await r.list('categories', { limit: 500 })).rows,
    [],
    ['categories'],
  ).data
  const sections = [...(all ?? [])].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
  )
  if (sections.length < 2) return null
  const move = (from: number, by: -1 | 1) =>
    act(async () => {
      const next = [...sections]
      const [moved] = next.splice(from, 1)
      next.splice(from + by, 0, moved!)
      // Every section gets its place, so two that shared one no longer fall back to the name.
      await repo.commit(
        next.flatMap((c, i) =>
          c.sort_order === i + 1 ? [] : [updateOp('categories', c, { sort_order: i + 1 })],
        ),
      )
    })
  return (
    <div>
      <h3 className="text-sm font-semibold">Order of the menu’s sections</h3>
      <p className="text-xs text-muted">Guests see the sections in this order.</p>
      <ol className="mt-2 max-w-md divide-y divide-border rounded-xl border border-border">
        {sections.map((c, i) => (
          <li key={c.id} className="flex items-center gap-1 py-1 pr-1 pl-3 text-sm">
            <span className="min-w-0 flex-1 truncate font-medium">{c.name}</span>
            <Button
              size="sm"
              variant="ghost"
              icon={<ChevronUp className="size-4" />}
              aria-label={`Move ${c.name} up`}
              disabled={disabled || i === 0}
              onClick={() => void move(i, -1)}
            />
            <Button
              size="sm"
              variant="ghost"
              icon={<ChevronDown className="size-4" />}
              aria-label={`Move ${c.name} down`}
              disabled={disabled || i === sections.length - 1}
              onClick={() => void move(i, 1)}
            />
          </li>
        ))}
      </ol>
    </div>
  )
}

/** Settings → Tables: switch QR ordering on, and print each table's code. */
export function QrOrderingCard({ settings }: { settings: TenantSettingsRow }) {
  const repo = useRepo()
  const toast = useToast()
  const online = useOnline()
  const { platform } = useSession()
  const { current } = useWorkspace()
  const canAdmin = useCan('admin')
  const canManage = useCan('manager')
  // Growth and above (and the trial): a plan with qr_ordering 0 does not have it.
  const inPlan = useEntitlements()?.limits?.qr_ordering !== 0
  const [codes, setCodes] = useState<TableCode[] | null>(null)
  const [busy, setBusy] = useState(false)
  const tenantId = current!.tenant_id
  const api = tenantApi(platform.supabase)
  const link = (token: string) => `${platform.menuUrl}/t/${token}`

  const act = async (work: () => Promise<void>) => {
    setBusy(true)
    try {
      await work()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const load = () =>
    act(async () => {
      const rows = await api.tableQrCodes(tenantId)
      setCodes(rows.map((r) => ({ table_id: r.table_id, name: r.name, url: link(r.token) })))
    })
  const renew = (code: TableCode) =>
    act(async () => {
      const { token } = await api.regenerateTableQr(tenantId, code.table_id)
      setCodes(
        (list) =>
          list?.map((c) => (c.table_id === code.table_id ? { ...c, url: link(token) } : c)) ?? null,
      )
      toast.success(`${code.name} has a new code. Print it again: the old one no longer works.`)
    })

  return (
    <Card>
      <h2 className="flex items-center gap-2 font-semibold">
        <QrIcon className="size-4" /> QR ordering
      </h2>
      <p className="mb-3 text-sm text-muted">
        Guests scan the code on their table, see the menu and send their order. Everyone at the
        table sees the same order, its kitchen status and the total so far. A waiter accepts each
        order before it joins the bill; accepting one from a free table starts that table.
      </p>
      {!inPlan && (
        <p className="mb-3 rounded-xl bg-warning-light p-3 text-sm text-warning">
          QR ordering is part of the Growth and Business plans.{' '}
          <Link to="/settings?tab=subscription" className="font-medium underline">
            See plans
          </Link>
        </p>
      )}
      <Switch
        className="max-w-md"
        label="Guests can order from the table"
        description="Switch it off to stop every code taking requests."
        checked={settings.guest_ordering && inPlan}
        disabled={!canAdmin || !inPlan || busy}
        onChange={(e) =>
          void act(() =>
            repo
              .commit([updateOp('tenant_settings', settings, { guest_ordering: e.target.checked })])
              .then(() => {}),
          )
        }
      />
      {inPlan && (
        <div className="mt-4 grid gap-4 border-t border-border pt-4">
          <div>
            <h3 className="text-sm font-semibold">Colour of the guest menu</h3>
            <p className="text-xs text-muted">
              The one colour of buttons and marks on the page guests open.
            </p>
            <div
              role="radiogroup"
              aria-label="Colour of the guest menu"
              className="mt-2 flex flex-wrap gap-2"
            >
              {GUEST_ACCENTS.map((a) => {
                const on = settings.guest_accent === a.id
                return (
                  <button
                    key={a.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={!canAdmin || busy}
                    onClick={() =>
                      void act(() =>
                        repo
                          .commit([updateOp('tenant_settings', settings, { guest_accent: a.id })])
                          .then(() => {}),
                      )
                    }
                    className={cx(
                      'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60',
                      on
                        ? 'border-primary/40 bg-primary-light font-semibold text-primary'
                        : 'border-border bg-surface text-muted hover:bg-soft hover:text-ink',
                    )}
                  >
                    {/* The page's own colour, not a token: this is what guests see. */}
                    <span
                      aria-hidden
                      className="size-4 rounded-full"
                      style={{ background: a.hex }}
                    />
                    {a.label}
                  </button>
                )
              })}
            </div>
          </div>
          <MenuSections disabled={!canManage || busy} act={act} />
        </div>
      )}
      {canManage && inPlan && (
        <div className="mt-4">
          {!codes ? (
            <Button
              variant="secondary"
              icon={<QrIcon className="size-4" />}
              loading={busy}
              disabled={!online}
              onClick={() => void load()}
            >
              Show the table codes
            </Button>
          ) : codes.length === 0 ? (
            <p className="text-sm text-muted">Add tables first; each one gets its own code.</p>
          ) : (
            <>
              <Button
                icon={<Printer className="size-4" />}
                onClick={() =>
                  printDocument(
                    <QrSheet codes={codes} businessName={settings.business_name} />,
                    'a4',
                  )
                }
              >
                Print QR codes
              </Button>
              {/* Until every shop has reprinted (docs/plans/menu-domain.md): then remove. */}
              <p className="mt-2 text-xs text-muted">
                The codes now open on {new URL(platform.menuUrl).host}. Codes printed before this
                change no longer work: print them again.
              </p>
              <ul className="mt-3 divide-y divide-border rounded-xl border border-border">
                {codes.map((c) => (
                  <li
                    key={c.table_id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                  >
                    {/* At least a few letters: on a phone the buttons wrap below instead. */}
                    <span className="min-w-16 flex-1 truncate font-semibold">{c.name}</span>
                    <TableLink name={c.name} url={c.url} />
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<RefreshCw className="size-3.5" />}
                      disabled={busy || !online}
                      aria-label={`New code for ${c.name}`}
                      onClick={() => void renew(c)}
                    >
                      New code
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
          {!online && (
            <p className="mt-2 text-xs text-muted">Connect to the internet to see the codes.</p>
          )}
        </div>
      )}
      {settings.guest_ordering && inPlan && (
        <p className="mt-3 text-xs text-muted">
          <Badge tone="success">On</Badge> Requests show on the Tables screen and on the table’s
          order.
        </p>
      )}
    </Card>
  )
}
